import { useTranslation } from "react-i18next";

import { getPersonRelation, type HaNPersonRow } from "@lib/logic";
import { getConnectedUserById, getConnectionById, getUser, useAppSelector } from "@lib/store";
import type { BuildingWorkspaceReservationResponse } from "@mapiq/user-api";

import { HaNDetailsPage } from "../HaNDetailsPage";
import { PersonConnectionAction } from "./components/PersonConnectionAction";
import { PersonDeskStatus } from "./components/PersonDeskStatus";
import { PersonHeader } from "./components/PersonHeader";
import { PersonOfficeDaysLink } from "./components/PersonOfficeDaysLink";
import { PersonToday } from "./components/PersonToday";

type PersonInfoPageProps = {
  person: HaNPersonRow["owner"];

  // The person's reservations in the building today, the earliest first. None when the search found them elsewhere.
  reservations: BuildingWorkspaceReservationResponse[];

  // The desk or area that the person was selected on, if any
  selectedNodeId: string | null;
};

export const PersonInfoPage = ({ person, reservations, selectedNodeId }: PersonInfoPageProps) => {
  const { t } = useTranslation();

  // The connected users also hold pending requests, so the connection's status says whether they're connected
  const connection = useAppSelector(state => {
    const connectionId = getConnectedUserById(state, person.id)?.connectionId;

    return connectionId ? getConnectionById(state, connectionId) : undefined;
  });

  const relation = getPersonRelation(connection);
  const isSelf = useAppSelector(state => getUser(state).id === person.id);

  // The desk the map shows them on, else the first desk they booked
  const desk = (
    reservations.find(r => r.workspace.deskId === selectedNodeId) ?? reservations.find(r => r.workspace.deskId)
  )?.workspace;

  return (
    <HaNDetailsPage title={t("hereAndNow:Coworker")}>
      <div className="flex flex-col gap-8 px-5 py-6">
        <PersonHeader person={person}>
          {!isSelf && (
            <PersonConnectionAction
              userId={person.id}
              connectionId={connection?.id ?? null}
              relation={relation}
            />
          )}
        </PersonHeader>

        <PersonToday reservations={reservations} />

        {/* Only a connection's page shows their office days right now */}
        {relation === "connected" && <PersonOfficeDaysLink userId={person.id} />}

        {desk?.deskId && desk.floorId && (
          <PersonDeskStatus
            buildingId={desk.buildingId}
            floorId={desk.floorId}
            deskId={desk.deskId}
          />
        )}
      </div>
    </HaNDetailsPage>
  );
};
